// Road damage (RALLY_GOAL.md §7: "ruts", "bumps", rough edges; Adam: potholes, washouts, puddles):
// small features too sharp for the ~10 m centreline control points, so they're applied inside the
// road profile itself — the rendered ribbon, the physics collider and every height query see the
// same shape. Seeded, so a stage is identical on every load and in the headless tests.
import { hash01 } from './noise';
import type { Track } from './types';

export type RoadFeatureSpec =
  /** A field of potholes over [from, to]; `water` = share that hold water (puddles). */
  | { type: 'potholes'; from: number; to: number; count: number; water?: number; size?: number; depth?: number }
  /** Deep wheel ruts over [from, to] (m). */
  | { type: 'ruts'; from: number; to: number; depth: number }
  /** A gully washed across the road at s, `skew` = along-shift per metre of lateral offset (diagonal). */
  | { type: 'washout'; s: number; depth: number; length: number; skew?: number };

export interface Pothole {
  s: number;
  d: number;
  r: number;
  depth: number;
  water: boolean;
}

export interface Washout {
  s: number;
  depth: number;
  half: number;
  skew: number;
}

export class RoadDamage {
  readonly potholes: Pothole[] = [];
  readonly washouts: Washout[] = [];
  /** Rut depth overrides: [from, to, depth]. */
  readonly ruts: [number, number, number][] = [];
  /** Per track sample: first index and count into `nearby` (potholes within reach of the sample). */
  private readonly start: Int32Array;
  private readonly count: Int32Array;
  private readonly nearby: number[] = [];

  constructor(
    private readonly track: Track,
    features: readonly RoadFeatureSpec[],
    seed: number,
  ) {
    const n = track.s.length;
    let k = 0;
    for (const f of features) {
      if (f.type === 'ruts') this.ruts.push([f.from, f.to, f.depth]);
      else if (f.type === 'washout') this.washouts.push({ s: f.s, depth: f.depth, half: f.length / 2, skew: f.skew ?? 0 });
      else {
        for (let j = 0; j < f.count; j++, k++) {
          const s = f.from + (f.to - f.from) * ((j + hash01(k, 71, seed)) / f.count);
          const i = Math.max(0, Math.min(n - 1, Math.round(s)));
          const hw = track.width[i] / 2;
          // Mostly in and between the wheel tracks, where traffic breaks the surface.
          const lane = hash01(k, 72, seed);
          const d = (lane < 0.5 ? -1 : 1) * (0.3 + 1.0 * hash01(k, 73, seed)) * Math.min(1, (hw - 0.6) / 1.4);
          const r = (f.size ?? 0.8) * (0.7 + 0.6 * hash01(k, 74, seed));
          const depth = (f.depth ?? 0.11) * (0.6 + 0.8 * hash01(k, 75, seed));
          this.potholes.push({ s, d, r, depth, water: hash01(k, 76, seed) < (f.water ?? 0) });
        }
      }
    }
    this.start = new Int32Array(n);
    this.count = new Int32Array(n);
    // Bucket potholes by the samples they can reach (a few metres either side).
    const lists: number[][] = [];
    this.potholes.forEach((p, idx) => {
      const a = Math.max(0, Math.floor(p.s - p.r - 2));
      const b = Math.min(n - 1, Math.ceil(p.s + p.r + 2));
      for (let i = a; i <= b; i++) (lists[i] ??= []).push(idx);
    });
    for (let i = 0; i < n; i++) {
      this.start[i] = this.nearby.length;
      const l = lists[i];
      if (l) this.nearby.push(...l);
      this.count[i] = this.nearby.length - this.start[i];
    }
  }

  /** Rut depth at s, or -1 where no override applies. */
  rutDepth(s: number): number {
    for (const [a, b, depth] of this.ruts) {
      if (s < a || s > b) continue;
      // Ease in and out over 8 m.
      const e = Math.min(1, (s - a) / 8, (b - s) / 8);
      return depth * Math.max(0, e);
    }
    return -1;
  }

  /** Along-track distance of (x, z) near sample i. */
  along(i: number, x: number, z: number): number {
    const t = this.track;
    return t.s[i] + (x - t.x[i]) * t.tx[i] + (z - t.z[i]) * t.tz[i];
  }

  /** How far below the road profile the surface is at sample i, lateral d, along-distance s (m). */
  depthAt(i: number, d: number, s: number): number {
    let depth = 0;
    for (let k = this.start[i], e = k + this.count[i]; k < e; k++) {
      const p = this.potholes[this.nearby[k]];
      const q = Math.hypot(s - p.s, d - p.d) / p.r;
      if (q >= 1) continue;
      // A bowl with a steep-ish rim.
      const b = 1 - q * q;
      depth = Math.max(depth, p.depth * b * Math.sqrt(b));
    }
    for (const w of this.washouts) {
      const u = (s - w.s - w.skew * d) / w.half;
      if (u <= -1 || u >= 1) continue;
      depth = Math.max(depth, w.depth * 0.5 * (1 + Math.cos(Math.PI * u)));
    }
    return depth;
  }

  /** Inside a puddle (a water-filled pothole), for the surface lookup. */
  waterAt(i: number, d: number, s: number): boolean {
    for (let k = this.start[i], e = k + this.count[i]; k < e; k++) {
      const p = this.potholes[this.nearby[k]];
      if (p.water && Math.hypot(s - p.s, d - p.d) < p.r * 0.72) return true;
    }
    return false;
  }

  /** 0…1 damp/dark tint for the road shader (pothole and washout bottoms). */
  wetness(i: number, d: number, s: number): number {
    const dep = this.depthAt(i, d, s);
    return Math.min(1, dep / 0.12);
  }
}
